import type { ReactNode } from 'react'
import { Button } from '../Button/Button'
import type { IconName } from '../Icon/types'
import { ProgressBar } from '../ProgressBar/ProgressBar'
import { cx } from '../utils/cx'
import './FlowLayout.css'

export interface FlowAction {
  label: ReactNode
  onClick: () => void
  icon?: IconName
  loading?: boolean
  disabled?: boolean
}

export interface FlowLayoutProps {
  /** Cabecera "Paso X de N · guardado"; sin ella (pantalla final) no hay barra */
  progress?: { step: number; total: number; backLabel: ReactNode; onBack: () => void; saved?: boolean } | undefined
  title: ReactNode
  intro?: ReactNode | undefined
  children?: ReactNode
  /** Acción principal fija abajo */
  primary?: FlowAction | undefined
  /** Enlace secundario bajo la acción principal */
  secondary?: FlowAction | undefined
  /** Mensaje junto a la acción (error al guardar), anunciado */
  footerMessage?: ReactNode | undefined
  /** Cabecera centrada con icono (pantalla "Todo listo") */
  hero?: ReactNode | undefined
  /** false dentro de otra página con su propio <main> (playground) */
  landmarks?: boolean
  className?: string
}

/**
 * Pantalla de flujo (bundle B2 y B9 §9b): cabecera con progreso y Atrás, un objetivo por pantalla,
 * acción principal fija abajo. Sin navegación. En escritorio, columna centrada de 640 px y botón
 * de 48 px que no ocupa todo el ancho.
 */
export function FlowLayout({ progress, title, intro, children, primary, secondary, footerMessage, hero, landmarks = true, className }: FlowLayoutProps) {
  const Header = landmarks ? 'header' : 'div'
  const Main = landmarks ? 'main' : 'div'
  const Footer = landmarks ? 'footer' : 'div'
  return (
    <div className={cx('hp-flow', className)}>
      {progress && (
        <Header className="hp-flow__header">
          <ProgressBar step={progress.step} total={progress.total} backLabel={progress.backLabel} onBack={progress.onBack} saved={progress.saved ?? true} />
        </Header>
      )}
      <Main className="hp-flow__body">
        {hero && <div className="hp-flow__hero">{hero}</div>}
        <h1 className={cx('hp-flow__title', hero !== undefined && 'hp-flow__title--center')}>{title}</h1>
        {intro && <p className={cx('hp-flow__intro', hero !== undefined && 'hp-flow__intro--center')}>{intro}</p>}
        {children}
      </Main>
      {(primary || secondary) && (
        <Footer className="hp-flow__footer">
          <div className="hp-flow__footer-inner">
            <div aria-live="polite" className="hp-flow__message">
              {footerMessage}
            </div>
            {primary && (
              <Button variant="primary" {...(primary.icon ? { icon: primary.icon } : {})} loading={primary.loading ?? false} disabled={primary.disabled ?? false} onClick={primary.onClick} className="hp-flow__primary">
                {primary.label}
              </Button>
            )}
            {secondary && (
              <button type="button" onClick={secondary.onClick} disabled={secondary.disabled ?? false} className="hp-flow__secondary">
                {secondary.label}
              </button>
            )}
          </div>
        </Footer>
      )}
    </div>
  )
}
